import React, { useMemo } from "react";
import { Clock, ExternalLink } from "lucide-react";

const PARTNER_PORTAL_URL =
  "https://partnerportal.fortinet.com/prm/English/s/assets?collectionId=67378&renderMode=Collection";

// Inner age badge — keeps the real freshness colour as secondary info inside
// the always-orange monthly reminder.
const AGE_BADGE = {
  fresh:   "bg-green-100 text-green-800 border border-green-200",
  review:  "bg-amber-100 text-amber-900 border border-amber-300",
  stale:   "bg-red-100 text-red-800 border border-red-300",
  unknown: "bg-zinc-100 text-zinc-700 border border-zinc-200",
};

function formatFrenchDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

const FR_MONTHS = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];

/**
 * Permanent monthly check-in reminder for the active price list.
 *
 * Always rendered in orange/amber on top of the PriceList page, with no
 * dismiss action — the partner must keep this reminder permanently visible
 * so the team is constantly nudged to verify the active price list against
 * the latest published edition on Partner Portal.
 */
export default function PricelistFreshnessBanner({ meta }) {
  const now = useMemo(() => new Date(), []);
  const monthLabel = `${FR_MONTHS[now.getUTCMonth()]} ${now.getUTCFullYear()}`;

  if (!meta) return null;

  const status = meta.freshness_status || "unknown";
  const ageBadgeClass = AGE_BADGE[status] || AGE_BADGE.unknown;

  return (
    <div
      className="relative border border-amber-400 bg-gradient-to-r from-amber-50 via-amber-100/70 to-amber-50 rounded-sm p-5 mb-3 shadow-sm"
      data-testid="pricelist-freshness-banner"
      role="region"
      aria-label={`Rappel mensuel — vérification de la liste de prix pour ${monthLabel}`}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 h-9 w-9 rounded-sm bg-amber-500 text-white flex items-center justify-center mt-0.5">
          <Clock className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">
              Rappel mensuel · {monthLabel}
            </span>
            {meta.age_days != null && (
              <span className={`px-2 py-0.5 text-[11px] rounded-sm font-mono font-semibold ${ageBadgeClass}`}>
                {meta.age_days} jour{meta.age_days > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <p className="mt-1 text-base font-semibold text-amber-950">
            Vérifiez que la liste de prix Fortinet est toujours à jour
          </p>
          <p className="text-xs text-amber-900 mt-1 leading-relaxed">
            Édition en cours : <span className="font-mono text-amber-950">{meta.filename || "—"}</span>
            {meta.effective_date && (
              <>
                {" "}· date d'effet <span className="font-semibold">{formatFrenchDate(meta.effective_date)}</span>
              </>
            )}
          </p>
          <p className="text-[11px] text-amber-800 mt-2 leading-relaxed">
            Fortinet publie en moyenne une nouvelle liste de prix chaque trimestre.
            Vérifiez systématiquement chaque mois calendaire qu'aucune édition plus
            récente n'a été diffusée — ce rappel reste affiché en permanence.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <a
              href={PARTNER_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-sm transition-colors"
              data-testid="pricelist-freshness-portal-link"
            >
              Vérifier sur PartnerPortal
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
